import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Flex,
  Image,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Text,
  Badge,
  Switch,
  FormControl,
  FormLabel,
  Button,
  Spacer,
  keyframes,
  Avatar,
  Spinner,
  useClipboard,
  VStack,
  HStack,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { FaBell, FaShoppingCart, FaExclamationTriangle, FaCopy, FaCheck } from 'react-icons/fa';

import NotificationCard from '../NotificationCard';
import AtalhosNavbar from '../AtalhosNavbar';
import { AuthContext } from '../../contexts/auth';
import { baseUrlApi } from '../../config';

import {
  getNotificacoes,
  getNovasNotificacoesApi,
  markNotificacaoAsRead,
  markNotificacaoAsUnread,
  markAllNotificacoesAsRead,
  markNotificacaoAsDisplayed,
} from '../../services/api';

const waveAnimation = keyframes`
  0% { transform: scale(0.9); opacity: 0.7; }
  100% { transform: scale(1.8); opacity: 0; }
`;

const MAX_VISIBLE_NOTIFICATIONS = 4;

// Função para formatar a data
const formatDateTime = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Meses são baseados em zero
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const NotificationCountBadge = ({ count }) => {
  if (count === 0) return null;

  return (
    <Badge
      position="absolute"
      bottom="-4px"
      left="-4px"
      bg="black"
      color="white"
      fontSize="0.7rem"
      fontWeight="bold"
      w="18px"
      h="18px"
      display="flex"
      alignItems="center"
      justifyContent="center"
      borderRadius="full"
      boxShadow="md"
      zIndex="2"
    >
      {count}
    </Badge>
  );
};

const extractIdsFromText = (texto) => {
  if (!texto) return [];
  const ids = new Set();
  const groupedMatches = texto.match(/\(([^)]+)\)/g);
  if (groupedMatches) {
    groupedMatches.forEach((group) => {
      const inner = group.slice(1, -1);
      inner.split(/[,\s]+/).forEach((part) => {
        const trimmed = part.trim();
        if (/^\d{4,}$/.test(trimmed)) {
          ids.add(trimmed);
        }
      });
    });
  }
  return Array.from(ids);
};

const getCopyValue = (notif) => {
  const idAuxStr = notif.id_aux ? String(notif.id_aux) : '';
  const isGrouped = !idAuxStr || idAuxStr === '0';
  if (isGrouped) {
    const ids = extractIdsFromText(notif.texto);
    if (ids.length > 0) return ids.join(', ');
  }
  return idAuxStr;
};

const NotificationItem = ({ notif, type, onToggleRead, updatingId }) => {
  const copyValue = getCopyValue(notif);
  const { onCopy, hasCopied } = useClipboard(copyValue);
  const idsExtraidos = extractIdsFromText(notif.texto);
  const isGrouped = (!notif.id_aux || String(notif.id_aux) === '0') && idsExtraidos.length > 0;
  const displayLabel = isGrouped
    ? `IDs (${idsExtraidos.length})`
    : notif.id_aux
      ? `ID: ${notif.id_aux}`
      : '';
  const showCopyRow = Boolean(copyValue);

  return (
    <MenuItem
      key={notif.id}
      closeOnSelect={false}
      py={3}
      bg={!notif.lida ? 'green.100' : 'transparent'}
      _hover={!notif.lida ? { bg: 'green.300' } : { bg: 'gray.200' }}
    >
      <HStack justify="space-between" align="stretch" w="100%" spacing={4}>
        {/* COLUNA DA ESQUERDA: Texto da notificação e ID */}
        <VStack align="flex-start" spacing={2} flex={1}>
          <Text
            fontSize="12px"
            color={notif.lida ? 'gray.500' : 'black'}
            fontWeight={notif.lida ? 'normal' : 'bold'}
          >
            {notif.texto}
          </Text>
          {showCopyRow && (
            <HStack spacing={2} onClick={(e) => e.stopPropagation()}>
              <Text fontSize="11px" color="gray.800" fontFamily="monospace">
                {displayLabel}
              </Text>
              <IconButton
                aria-label={isGrouped ? 'Copiar todos os IDs' : 'Copiar ID'}
                icon={hasCopied ? <FaCheck color="green" /> : <FaCopy />}
                onClick={onCopy}
                size="xs"
                variant="ghost"
              />
            </HStack>
          )}
        </VStack>
        {/* COLUNA DA DIREITA: Data e Seletor */}
        <VStack justify="space-between" align="flex-end">
          <Text
            fontSize="10px"
            color="gray.600"
            whiteSpace="nowrap"
          >
            {formatDateTime(notif.created_at)}
          </Text>
          {updatingId === notif.id ? (
            <Spinner size="sm" />
          ) : (
            <FormControl display="flex" alignItems="center" w="auto">
              <FormLabel htmlFor={`${type}-read-${notif.id}`} mb="0" fontSize="sm">
                {notif.lida ? 'Lida' : 'Não Lida'}
              </FormLabel>
              <Switch
                id={`${type}-read-${notif.id}`}
                isChecked={notif.lida}
                onChange={() => onToggleRead(type, notif.id)}
                size="sm"
              />
            </FormControl>
          )}
        </VStack>
      </HStack>
    </MenuItem>
  );
};

const NavbarTop = () => {
  const { user } = useContext(AuthContext);
  const [updatingNotificationId, setUpdatingNotificationId] = useState(null);

  const [showLiveNotifications, setShowLiveNotifications] = useState(false);

  const getImgUser = () => {
    if (!user) return "/user.png";
    const userImages = {
      3: "/gabriel_logo.webp",
      5: "/tilio_logo.webp",
      6: "/riccioppo_logo.webp",
      8: "/gabriel_valiente_logo.webp",
      9: "/emerson_vendedor_logo.webp",
      10: "/analuiza_logo.webp"
    };
    return userImages[user.id] || "/user.png";
  };

  const [newSalesNotifications, setNewSalesNotifications] = useState([]);
  const [warningNotifications, setWarningNotifications] = useState([]);
  const [miscNotifications, setMiscNotifications] = useState([]);
  const [liveNotifications, setLiveNotifications] = useState([]);

  const pendingNotificationsQueue = useRef([]);
  const nextLiveNotificationId = useRef(0);
  const currentlyManagedNotificationsRef = useRef(new Set());

  const markAsViewedInBackend = useCallback(async (notificationOriginalId, notificationType) => {
    const success = await markNotificacaoAsDisplayed(notificationOriginalId);
    if (success) {
      currentlyManagedNotificationsRef.current.add(`${notificationType}-${notificationOriginalId}`);
    } else {
      console.error(`Falha ao marcar notificação ${notificationOriginalId} como exibida.`);
    }
  }, []);

  // LÓGICA ATUALIZADA
  const displayNextQueuedNotification = useCallback(() => {
    // Só exibe se a opção estiver ATIVA
    if (!showLiveNotifications) {
      return;
    }
    setLiveNotifications(prevLive => {
      if (prevLive.length < MAX_VISIBLE_NOTIFICATIONS && pendingNotificationsQueue.current.length > 0) {
        const nextNotif = pendingNotificationsQueue.current.shift();
        markAsViewedInBackend(nextNotif.id, nextNotif.type);
        return [...prevLive, { ...nextNotif, liveId: nextLiveNotificationId.current++ }];
      }
      return prevLive;
    });
  }, [markAsViewedInBackend, showLiveNotifications]); // Adicionada dependência

  const handleLiveNotificationRemoval = useCallback((liveIdRemoved) => {
    const notifToRemove = liveNotifications.find(n => n.liveId === liveIdRemoved);
    if (notifToRemove) {
      currentlyManagedNotificationsRef.current.delete(`${notifToRemove.type}-${notifToRemove.id}`);
    }
    setLiveNotifications(prev => prev.filter(n => n.liveId !== liveIdRemoved));
    setTimeout(displayNextQueuedNotification, 0);
  }, [liveNotifications, displayNextQueuedNotification]);
  
  // NOVO useEffect para limpar notificações quando a opção é desativada
  useEffect(() => {
    if (!showLiveNotifications) {
      setLiveNotifications([]);
      pendingNotificationsQueue.current = [];
    }
  }, [showLiveNotifications]);


  const toggleNotificationRead = async (type, id) => {
    //... (código original sem alterações)
    setUpdatingNotificationId(id);
    const typeMap = {
      sales: { list: newSalesNotifications, setter: setNewSalesNotifications },
      warnings: { list: warningNotifications, setter: setWarningNotifications },
      misc: { list: miscNotifications, setter: setMiscNotifications },
    };
    const { list, setter } = typeMap[type];
    const notifToUpdate = list.find(notif => notif.id === id);
    if (!notifToUpdate) {
      setUpdatingNotificationId(null);
      return;
    }
    const newReadStatus = !notifToUpdate.lida;
    let success = false;
    if (newReadStatus === true) {
      success = await markNotificacaoAsRead(id);
    } else {
      success = await markNotificacaoAsUnread(id);
    }
    if (success) {
      const updatedNotifications = list.map(notif =>
        notif.id === id ? { ...notif, lida: newReadStatus } : notif
      );
      setter(updatedNotifications);
    } else {
      console.error(`Falha ao alterar o status da notificação ${id}.`);
    }
    setUpdatingNotificationId(null);
  };

  const markAllAsRead = async (type) => {
    //... (código original sem alterações)
    const typeMap = {
      sales: { list: newSalesNotifications, setter: setNewSalesNotifications, typeString: 'venda' },
      warnings: { list: warningNotifications, setter: setWarningNotifications, typeString: 'alerta' },
      misc: { list: miscNotifications, setter: setMiscNotifications, typeString: 'aviso' },
    };
    const { list, setter, typeString } = typeMap[type];
    const idsToMark = list.filter(notif => !notif.lida).map(notif => notif.id);
    if (idsToMark.length === 0) return;
    const success = await markAllNotificacoesAsRead(idsToMark);
    if (success) {
      setter(prev => prev.map(notif => ({ ...notif, lida: true })));
      const cleanFromManaged = (id) => currentlyManagedNotificationsRef.current.delete(`${typeString}-${id}`);
      setLiveNotifications(prev => prev.filter(notif => notif.type !== typeString || (cleanFromManaged(notif.id), false)));
      pendingNotificationsQueue.current = pendingNotificationsQueue.current.filter(notif => notif.type !== typeString || (cleanFromManaged(notif.id), false));
      setTimeout(displayNextQueuedNotification, 0);
    } else {
      console.error(`Falha ao marcar todas as notificações ${type} como lidas.`);
    }
  };

  const unreadSalesCount = newSalesNotifications.filter(n => !n.lida).length;
  const unreadWarningsCount = warningNotifications.filter(n => !n.lida).length;
  const unreadMiscCount = miscNotifications.filter(n => !n.lida).length;

  // LÓGICA DE FETCH ATUALIZADA
  const fetchNotifications = useCallback(async () => {

    if(baseUrlApi == "http://127.0.0.1:8000/api" || baseUrlApi == "http://127.0.0.1:8080/api") {
      return;
    }

    if (!user) return;
    
    // Só busca e enfileira novas notificações se a exibição estiver ativa
    if (showLiveNotifications) {
        const notificationsFromApi = await getNovasNotificacoesApi();
        if (notificationsFromApi) {
            notificationsFromApi.forEach(notif => {
                const typeString = notif.tipo === 0 ? 'venda' : (notif.tipo === 1 ? 'alerta' : 'aviso');
                const uniqueKey = `${typeString}-${notif.id}`;
                if (!currentlyManagedNotificationsRef.current.has(uniqueKey)) {
                    pendingNotificationsQueue.current.push({ id: notif.id, type: typeString, message: notif.texto, id_aux: notif.id_aux, created_at: notif.created_at });
                    currentlyManagedNotificationsRef.current.add(uniqueKey);
                }
            });
            setTimeout(displayNextQueuedNotification, 0);
        }
    }
    
    // Ordena por created_at desc (mais recentes primeiro) como salvaguarda
    const sortByRecent = (list) =>
      [...list].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    // As buscas abaixo populam as listas dos menus.
    // Em paralelo (Promise.all) para reduzir o tempo do ciclo e agrupar os re-renders.
    const [allSales, allWarnings, allMisc] = await Promise.all([
      getNotificacoes({ tipo: 0 }),
      getNotificacoes({ tipo: 1 }),
      getNotificacoes({ tipo: 2 }),
    ]);
    if (allSales) setNewSalesNotifications(sortByRecent(allSales));
    if (allWarnings) setWarningNotifications(sortByRecent(allWarnings));
    if (allMisc) setMiscNotifications(sortByRecent(allMisc));
  }, [user, displayNextQueuedNotification, showLiveNotifications]); // Adicionada dependência

  useEffect(() => {
    fetchNotifications();
    // Polling a cada 90s, mas só quando a aba está visível (economiza requisições de abas em segundo plano).
    const interval = setInterval(() => {
      if (!document.hidden) fetchNotifications();
    }, 90000);
    // Ao voltar para a aba, atualiza na hora para não esperar o próximo ciclo.
    const onVisibilityChange = () => {
      if (!document.hidden) fetchNotifications();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [fetchNotifications]);

  const renderNotificationList = (type, notifications, unreadCount) => {
    //... (código original sem alterações)
    const titles = { sales: 'Vendas', warnings: 'Alertas', misc: 'Avisos' };
    return (
      <>
        <Flex justifyContent="space-between" alignItems="center" px={4} py={2}>
          <Text fontWeight="bold">{titles[type]} ({unreadCount})</Text>
          {unreadCount > 0 && <Button size="xs" onClick={() => markAllAsRead(type)}>Marcar todas como lidas</Button>}
        </Flex>
        <MenuDivider />
        {notifications.length > 0 ? (
          notifications.map(notif => (
            <NotificationItem
              key={notif.id}
              notif={notif}
              type={type}
              onToggleRead={toggleNotificationRead}
              updatingId={updatingNotificationId}
            />
          ))
        ) : (
          <MenuItem isDisabled>Nenhuma notificação.</MenuItem>
        )}
      </>
    );
  };

  return (
    <>
      <Box
        bg="white"
        px={{ base: 4, md: 8 }}
        py={1}
        boxShadow="lg"
        position="sticky"
        top="0"
        zIndex="999"
      >
        <Flex h={12} alignItems="center" justifyContent="space-between">
          <Box borderRadius="lg" w={{ base: "90px", md: "100px" }} h="38px" mr={{ base: 2, md: 4 }} flexShrink={0}>
            <Link to="/"><Image w="100%" h="100%" p="1" src="/TAGANOBG.png" alt="Logo da Empresa" objectFit="contain" /></Link>
          </Box>
          <AtalhosNavbar />
          <Spacer />
          <Flex alignItems="center" mr={{ base: 2, md: 4 }}>
            {/* ... Menus de notificação de Vendas, Alertas e Avisos (código original sem alterações) ... */}
            <Menu>
              <Box position="relative" mr={3}>
                <MenuButton
                  as={IconButton} icon={<FaShoppingCart />} variant="solid"
                  bg={unreadSalesCount > 0 ? "#F68633" : "gray.300"}
                  color={unreadSalesCount > 0 ? "white" : "gray.600"}
                  _hover={{ bg: unreadSalesCount > 0 ? "#e07a2c" : "gray.400" }}
                  borderRadius="full" size="md" w="38px" h="38px" _focus={{ boxShadow: 'none' }}
                  _after={unreadSalesCount > 0 ? { content: '""', position: 'absolute', top: '0', left: '0', width: '100%', height: '100%', borderRadius: 'full', bg: "#F68633", zIndex: -1, animation: `${waveAnimation} 2s infinite` } : undefined}
                />
                <NotificationCountBadge count={unreadSalesCount} />
              </Box>
              <MenuList maxH="300px" overflowY="auto" w={{ base: "90vw", md: "384px" }}>
                {renderNotificationList('sales', newSalesNotifications, unreadSalesCount)}
              </MenuList>
            </Menu>
            <Menu>
              <Box position="relative" mr={3}>
                <MenuButton
                  as={IconButton} icon={<FaExclamationTriangle />} variant="solid"
                  bg={unreadWarningsCount > 0 ? "red.500" : "gray.300"}
                  color={unreadWarningsCount > 0 ? "white" : "gray.600"}
                  _hover={{ bg: unreadWarningsCount > 0 ? "red.600" : "gray.400" }}
                  borderRadius="full" size="md" w="38px" h="38px" _focus={{ boxShadow: 'none' }}
                  _after={unreadWarningsCount > 0 ? { content: '""', position: 'absolute', top: '0', left: '0', width: '100%', height: '100%', borderRadius: 'full', bg: "red.500", zIndex: -1, animation: `${waveAnimation} 2s infinite` } : undefined}
                />
                <NotificationCountBadge count={unreadWarningsCount} />
              </Box>
              <MenuList maxH="300px" overflowY="auto" w={{ base: "90vw", md: "384px" }}>
                {renderNotificationList('warnings', warningNotifications, unreadWarningsCount)}
              </MenuList>
            </Menu>
            <Menu>
              <Box position="relative">
                <MenuButton
                  as={IconButton} icon={<FaBell />} variant="solid"
                  bg={unreadMiscCount > 0 ? "blue.500" : "gray.300"}
                  color={unreadMiscCount > 0 ? "white" : "gray.600"}
                  _hover={{ bg: unreadMiscCount > 0 ? "blue.600" : "gray.400" }}
                  borderRadius="full" size="md" w="38px" h="38px" _focus={{ boxShadow: 'none' }}
                  _after={unreadMiscCount > 0 ? { content: '""', position: 'absolute', top: '0', left: '0', width: '100%', height: '100%', borderRadius: 'full', bg: "blue.500", zIndex: -1, animation: `${waveAnimation} 2s infinite` } : undefined}
                />
                <NotificationCountBadge count={unreadMiscCount} />
              </Box>
              <MenuList maxH="300px" overflowY="auto" w={{ base: "90vw", md: "384px" }}>
                {renderNotificationList('misc', miscNotifications, unreadMiscCount)}
              </MenuList>
            </Menu>
          </Flex>

          {/* MENU DO USUÁRIO ATUALIZADO COM O SWITCH */}
          <Menu>
            <MenuButton variant="ghost" aria-label="Opções do Usuário" borderRadius="full" p="0">
              <Avatar size="md" name={user?.name || "Usuário"} src={getImgUser()} />
            </MenuButton>
            {/* <MenuList>
              <MenuItem as="div" closeOnSelect={false} _hover={{ bg: 'transparent' }} _focus={{ bg: 'transparent' }}>
                <FormControl display="flex" alignItems="center">
                  <FormLabel htmlFor="show-notifications-switch" mb="0" fontSize="sm">
                    Notificações na tela
                  </FormLabel>
                  <Switch
                    id="show-notifications-switch"
                    isChecked={showLiveNotifications}
                    onChange={() => setShowLiveNotifications(!showLiveNotifications)}
                  />
                </FormControl>
              </MenuItem>
            </MenuList> */}
          </Menu>

        </Flex>

        <Box position="fixed" bottom="20px" right="20px" zIndex="1000" display="flex" flexDirection="column-reverse" alignItems="flex-end">
          {liveNotifications.map(notif => (
            <NotificationCard
              key={notif.liveId}
              liveId={notif.liveId}
              id={notif.id}
              type={notif.type}
              message={notif.message}
              createdAt={notif.created_at}
              onClose={handleLiveNotificationRemoval}
              onMarkAsViewedBackend={markAsViewedInBackend}
            />
          ))}
        </Box>
      </Box>
    </>
  );
};

export default NavbarTop;